/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { createMutationState } from '@/utils/query/createMutationState';
import { getAxios } from '@/lib/axios';
import { requestBaseURL } from '@/config/serviceAPI';
import { getSalesChannel } from '@/utils/common';
import { cardVendors } from '@/constants/cardVendors';
import type { AxiosInstance } from 'axios';
import type { PaymentOptionList } from '@/types';

const URL = `${requestBaseURL}/ecomm/b2c/v1/order/payment/options`;

type PaymentOptionsAPIResponse = {
  paymentOptionList: Array<{
    paymentMethod: string;
    numberOfCardsAllowed: number;
  }>;
};

/**
 * API function to fetch payment options for the current sales channel
 */
const getPaymentOptions = async (
  _options: Record<string, never>,
  axiosInstance: AxiosInstance | undefined,
): Promise<PaymentOptionList> => {
  const salesChannel = getSalesChannel();

  const response = await getAxios(axiosInstance).get<PaymentOptionsAPIResponse>(URL, {
    params: {
      salesChannel,
    },
  });

  return transformPaymentOptions(response.data.paymentOptionList);
};

const transformPaymentOptions = (
  apiPaymentOptionList: PaymentOptionsAPIResponse['paymentOptionList'],
): PaymentOptionList => {
  const paymentOptionObject: PaymentOptionList = {};

  apiPaymentOptionList?.forEach((item) => {
    if (!(item.numberOfCardsAllowed > 0)) {
      return;
    }

    if (item.paymentMethod === cardVendors.giftCard) {
      paymentOptionObject.giftCardEnabled = true;
      paymentOptionObject.noOfGiftCardAllowed = item.numberOfCardsAllowed;
    } else if (item.paymentMethod === cardVendors.merchCard) {
      paymentOptionObject.merchCardEnabled = true;
      paymentOptionObject.noOfMerchCardAllowed = item.numberOfCardsAllowed;
    } else if (item.paymentMethod === cardVendors.loyaltyCard) {
      paymentOptionObject.rewardsEnabled = true;
      paymentOptionObject.noOfRewardsEnabled = item.numberOfCardsAllowed;
    } else if (item.paymentMethod === cardVendors.amexCard) {
      paymentOptionObject.amexEnabled = true;
      paymentOptionObject.noOfAmexEnabled = item.numberOfCardsAllowed;
    } else if (item.paymentMethod === cardVendors.masterCard) {
      paymentOptionObject.masterEnabled = true;
      paymentOptionObject.noOfMasterEnabled = item.numberOfCardsAllowed;
    } else if (item.paymentMethod === cardVendors.visaCard) {
      paymentOptionObject.visaEnabled = true;
      paymentOptionObject.noOfVisaEnabled = item.numberOfCardsAllowed;
    } else if (item.paymentMethod === cardVendors.paypal) {
      paymentOptionObject.paypalEnabled = true;
      paymentOptionObject.noOfPaypalEnabled = item.numberOfCardsAllowed;
    } else if (item.paymentMethod === cardVendors.discoverCard) {
      paymentOptionObject.discoverEnabled = true;
      paymentOptionObject.noOfDiscoverEnabled = item.numberOfCardsAllowed;
    } else if (item.paymentMethod === cardVendors.googlePay) {
      paymentOptionObject.googlePayEnabled = true;
    } else if (item.paymentMethod === cardVendors.applePay) {
      paymentOptionObject.applePayEnabled = true;
    }
  });

  return paymentOptionObject;
};

const { useMutation, useMutationState } = createMutationState(
  ['checkout', 'paymentOptions'],
  getPaymentOptions,
  {
    ensureOnlyOneMutation: true,
  },
);

export const usePaymentOptionsMutation = () => {
  return useMutation({});
};

export const usePaymentOptions = () => {
  return useMutationState();
};
